import { useMemo } from "react";
import { useScheduleStats } from "@hooks/schedule/useScheduleStats.ts";
import TdSummaryPerNurse from "@components/feature/rosterTable/TdSummaryPerNurse.tsx";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import { useGroupStore } from "@/store/groupStore";
import { useIsAdmin } from "@/hooks/useAuth";
import { useSortShift } from "@/hooks/shift/useSortShift";
import {
  aggregateCountKeysByShiftDefinitions,
  getShiftColumnGroupKey,
  normalizeScheduleCellCode,
  resolveShiftFromScheduleCode,
  resolveShiftFromScheduleCellFromMaps,
  type ShiftLookupMaps,
} from "@/utils/shift-util";
import type { ShiftCatalogShift } from "@/services/shift/shiftCatalog";

function countForShiftColumn(
  scheduleStats: Record<string, number>,
  shift: ShiftCatalogShift
): number {
  const byGroup = scheduleStats[getShiftColumnGroupKey(shift)];
  if (byGroup != null) return byGroup;
  if (shift.id != null && Number.isFinite(Number(shift.id))) {
    const byId = scheduleStats[`id:${shift.id}`];
    if (byId != null) return byId;
  }
  return scheduleStats[shift.shift_id] ?? 0;
}

export default function ShiftStatus({
  nurseSchedule,
  scheduleIds,
  shiftDefinitions,
  visibleShifts,
  columnShiftIds,
  /** 헤더와 동일한 열(시프트 행) — 있으면 동일 shift_id도 DB id로 구분해 집계 */
  columnShifts,
  shiftLookupMaps,
  counts,
}: {
  nurseSchedule?: (string | null | { code: string | null } | null)[];
  shiftDefinitions?: ShiftCatalogShift[];
  scheduleIds?: (number | null)[] | null;
  visibleShifts?: string[];
  columnShiftIds?: string[];
  columnShifts?: ShiftCatalogShift[];
  shiftLookupMaps?: ShiftLookupMaps<ShiftCatalogShift>;
  counts?: { [key: string]: number | null } | null;
}) {
  const selectedGroupId = useGroupStore((state) => state.selectedGroupId);
  const isAdmin = useIsAdmin();

  const { data: shiftsRes } = useShiftsQuery(
    isAdmin ? (selectedGroupId ?? "") : ""
  );

  const shiftDefsFromQuery = useMemo(() => shiftsRes ?? [], [shiftsRes]);

  const shiftDefs = shiftDefinitions?.length
    ? shiftDefinitions
    : shiftDefsFromQuery;

  /** `[]`이면 "사용된 열 없음" — `undefined`일 때만 레거시(전체 코드) */
  const statsKeyMode = columnShifts != null ? "shift_db_id" : "shift_id";

  const scheduleStatsFromHook = useScheduleStats(
    nurseSchedule ?? [],
    shiftDefs,
    scheduleIds,
    statsKeyMode
  );

  const scheduleStatsFromLookupMaps = useMemo(() => {
    if (!nurseSchedule || nurseSchedule.length === 0 || !shiftLookupMaps) {
      return null;
    }

    const useDbId = statsKeyMode === "shift_db_id";

    return nurseSchedule.reduce<Record<string, number>>((acc, shift, index) => {
      const resolved = resolveShiftFromScheduleCellFromMaps(
        normalizeScheduleCellCode(shift),
        scheduleIds?.[index] ?? null,
        shiftLookupMaps
      );
      if (!resolved) return acc;

      const key =
        useDbId && resolved.id != null && Number.isFinite(Number(resolved.id))
          ? getShiftColumnGroupKey(resolved)
          : resolved.shift_id;
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {});
  }, [nurseSchedule, scheduleIds, shiftLookupMaps, statsKeyMode]);

  const scheduleStats = useMemo(() => {
    if (nurseSchedule && nurseSchedule.length > 0) {
      return scheduleStatsFromLookupMaps ?? scheduleStatsFromHook;
    }
    if (columnShifts != null) {
      return aggregateCountKeysByShiftDefinitions(
        counts ?? undefined,
        shiftDefs,
        {
          keyMode: "shift_db_id",
        }
      );
    }
    return aggregateCountKeysByShiftDefinitions(counts ?? undefined, shiftDefs);
  }, [
    nurseSchedule,
    scheduleStatsFromHook,
    scheduleStatsFromLookupMaps,
    counts,
    shiftDefs,
    columnShifts,
  ]);

  const sortedShifts = useSortShift(
    (shiftDefinitions?.length ? shiftDefinitions : shiftsRes) ?? []
  );
  const sortedShiftIds = useMemo(
    () => sortedShifts.map((shift) => shift.shift_id),
    [sortedShifts]
  );

  const renderCodes = useMemo(() => {
    if (columnShiftIds && columnShiftIds.length > 0) {
      return columnShiftIds;
    }
    if (visibleShifts && visibleShifts.length > 0) {
      const normalized = visibleShifts.map((code) => {
        const resolved = resolveShiftFromScheduleCode(code, shiftDefs);
        return resolved?.shift_id ?? code;
      });
      return Array.from(new Set(normalized));
    }
    return sortedShiftIds;
  }, [columnShiftIds, visibleShifts, sortedShiftIds, shiftDefs]);

  const codeToDefault = useMemo(() => {
    const map = new Map<string, string>();
    shiftDefs.forEach((shift) => {
      if (!shift.shift_id) return;
      const base = shift.default_shift || shift.shift_id;
      map.set(shift.shift_id, base);
      if (shift.default_shift) {
        map.set(shift.default_shift, shift.default_shift);
      }
    });
    return map;
  }, [shiftDefs]);

  const getCountForCode = (code: string) => {
    const targetBase = codeToDefault.get(code) ?? code;
    let total = 0;
    Object.entries(scheduleStats).forEach(([key, value]) => {
      const keyBase = codeToDefault.get(key) ?? key;
      if (keyBase === targetBase) {
        total += value ?? 0;
      }
    });
    return total;
  };

  /** 헤더 `RosterShiftLegendChips`와 동일: gap·열 너비 정합 */
  const statsRowClass = "flex items-center gap-[5.7px]";

  if (columnShifts != null) {
    return (
      <div className={statsRowClass}>
        {columnShifts.map((shift) => (
          <TdSummaryPerNurse
            key={shift.id != null ? `id-${shift.id}` : `sid-${shift.shift_id}`}
            code={String(shift.id ?? shift.shift_id)}
            count={countForShiftColumn(scheduleStats, shift)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={statsRowClass}>
      {renderCodes.map((code) => (
        <TdSummaryPerNurse
          key={code}
          code={code}
          count={getCountForCode(code)}
        />
      ))}
    </div>
  );
}
